// checkout/steps/paymentMethodRenderer.ts
import { toggleLoadingSpinner } from '../utils/toggleLoadingSpinner';

/**
 * Make dynamically inserted payment labels focusable and ARIA-correct.
 *
 * @param {ParentNode} root - Container that holds the payment options.
 * @returns {void}
 */
function enhancePaymentLabels(root: ParentNode): void {
	const labels = root.querySelectorAll<HTMLLabelElement>('label.payment__option--label[for]');

	labels.forEach((label) => {
		const forId = label.getAttribute('for');
		if (!forId) return;

		const input =
			(label.control as HTMLInputElement | null) ||
			root.querySelector<HTMLInputElement>('#' + forId);
		if (!input) return;

		// Label becomes the focusable radio
		label.setAttribute('role', 'radio');
		label.setAttribute('tabindex', '0');
		label.setAttribute('aria-checked', input.checked ? 'true' : 'false');
		if (input.disabled) label.setAttribute('aria-disabled', 'true');
		else label.removeAttribute('aria-disabled');

		// Keep native input out of tab order but present for submission
		input.tabIndex = -1;
		input.setAttribute('aria-hidden', 'true');
	});
}

/**
 * Inject registered-card radios and the PayPal option.
 *
 * @param {any} data - Parsed JSON from #step-json-paymentMethod
 * @returns {void}
 */
export function renderPaymentOptions(data: any): void {
	const form = document.getElementById('paymentMethodForm') as HTMLFormElement | null;
	const registeredContainer = document.getElementById(
		'payment-method-registered-options',
	) as HTMLElement | null;
	const registeredLabel = document.getElementById(
		'payment-method-options-existing-label',
	) as HTMLElement;
	const paypalContainer = document.getElementById(
		'payment-method-paypal-option',
	) as HTMLElement | null;
	const googlePayContainer = document.getElementById(
		'payment-method-google-pay-option',
	) as HTMLElement | null;
	const applePayContainer = document.getElementById(
		'payment-method-apple-pay-option',
	) as HTMLElement | null;
	const payByCardLabel = document.getElementById(
		'new-payment-method-braintree-card-label',
	) as HTMLElement;
	if (!registeredContainer || !paypalContainer || !googlePayContainer || !applePayContainer)
		return;

	const base = (window as any).serverData?.contextPath || '';
	const selected = data?.fields?.paymentMethod || '';
	const checkoutPaymentElem = document.querySelector('.checkout__payment') as HTMLElement;

	//Initialize the spinner
	toggleLoadingSpinner(checkoutPaymentElem, 'Loading Payment Methods');

	// Registered cards
	registeredContainer.innerHTML = '';
	if (Array.isArray(data?.wallet) && data.wallet.length) {
		data.wallet.forEach((entry: any) => {
			if (entry?.invisible) return;

			const id = `walletEntry${entry.id}`;
			const value = `wallet_${entry.id}`;
			const type = (entry.cardType ?? 'Card').toString().replace(/\s+/g, '');
			const last4 = String(entry.cardNumber || '').slice(-4);
			const labelText = `•••• ${last4} (Exp. ${entry.expiryMonth}/${entry.expiryYear})`;
			const checked = selected === value ? 'checked' : '';
			const disabled = entry.disabled ? 'disabled' : '';

			registeredContainer.insertAdjacentHTML(
				'beforeend',
				`
				<div class="form__row form__row--payment form__row--payment-card" role="none">
					<div class="payment__option payment__option-card" role="none">
						<label class="payment__option--label" for="${id}">
							<span class="payment__option--name">${labelText}</span>
							<input
								class="payment__input hidden__screen-reader"
								name="paymentMethod"
								type="radio"
								id="${id}"
								value="${value}"
								aria-describedby="paymentMethod-error"
								${disabled}
								${checked}>
							<span class="payment__option--logos">
								<img src="${base}/skin/images/checkout/${type}_icon.svg" alt="${type}">
							</span>
						</label>
					</div>
				</div>
				`,
			);
		});
	}

	// Show/hide registered container based on content
	if (registeredContainer.querySelector('input[name="paymentMethod"]')) {
		registeredContainer.removeAttribute('hidden');
		registeredLabel.removeAttribute('hidden');
		payByCardLabel.innerText = 'Add a new card';
	} else {
		registeredContainer.setAttribute('hidden', '');
		registeredLabel.setAttribute('hidden', '');
	}

	// PayPal
	paypalContainer.innerHTML = '';
	if (data?.paypalEnabled === 'true' && data?.paypal) {
		const paypalValue = data.paypal.value || 'paypal';
		const paypalChecked = data.paypal.checked === 'true' ? 'checked' : '';

		paypalContainer.insertAdjacentHTML(
			'beforeend',
			`
			<div class="form__row form__row--payment form__row--payment-paypal" role="none">
				<div class="payment__option payment__option-paypal" role="none">
					<label class="payment__option--label" for="radio-paypal-payment">
						<span class="payment__option--name">PayPal</span>
						<input
							class="payment__input hidden__screen-reader"
							name="paymentMethod"
							type="radio"
							id="radio-paypal-payment"
							value="${paypalValue}"
							aria-describedby="paymentMethod-error"
							${paypalChecked}>
						<span class="payment__option--logos">
							<img src="${base}/skin/images/checkout/PayPal_icon.svg" alt="PayPal">
						</span>
					</label>
				</div>
			</div>
			`,
		);
	}

	// Show/hide PayPal container based on content
	if (paypalContainer.querySelector('input[name="paymentMethod"]')) {
		paypalContainer.removeAttribute('hidden');
	} else {
		paypalContainer.setAttribute('hidden', '');
	}

	window.braintreeFunctions!.doesBrowserSupportGooglePay().then((result) => {
		if (result) {
			googlePayContainer.innerHTML = '';
			if (data.googlePayEnabled === 'true' && data.googlePay) {
				const checked = data.googlePay.checked === 'true' ? 'checked' : '';
				const googlePayValue = data.googlePay.value || 'google-pay';
				if (!googlePayContainer) return;
				googlePayContainer?.insertAdjacentHTML(
					'beforeend',
					`
			<div class="form__row form__row--payment form__row--payment-google-pay">
				<div class="payment__option payment__option-google-pay">
					<label class="payment__option--label" for="radio-google-pay-payment">
						<span class="payment__option--name">Google Pay</span>
						<input class="payment__input hidden__screen-reader" 
							   name="paymentMethod" 
							   type="radio" 
							   id="radio-google-pay-payment" 
								value="${googlePayValue}"
								${checked}>
						<span class="payment__option--logos">
							<img src="${base}/skin/images/checkout/Google_Pay_icon.svg" alt="GooglePay" />
						</span>
					</label>
				</div>
			</div>
		`,
				);
			}

			// Show/hide Google Pay container based on content
			if (googlePayContainer.querySelector('input[name="paymentMethod"]')) {
				googlePayContainer.removeAttribute('hidden');
			} else {
				googlePayContainer.setAttribute('hidden', '');
			}
			enhancePaymentLabels(googlePayContainer);
		}
	});

	window.braintreeFunctions!.doesBrowserSupportApplePay().then((result) => {
		if (result) {
			applePayContainer.innerHTML = '';
			if (data.applePayEnabled === 'true' && data.applePay) {
				const checked = data.applePay.checked === 'true' ? 'checked' : '';
				const applePayValue = data.applePay.value || 'apple-pay';
				if (!applePayContainer) return;
				applePayContainer?.insertAdjacentHTML(
					'beforeend',
					`
			<div class="form__row form__row--payment form__row--payment-apple-pay">
				<div class="payment__option payment__option-apple-pay">
					<label class="payment__option--label" for="radio-apple-pay-payment">
						<span class="payment__option--name">Apple Pay</span>
						<input class="payment__input hidden__screen-reader" 
							   name="paymentMethod" 
							   type="radio" 
							   id="radio-apple-pay-payment" 
								value="${applePayValue}"
								${checked}>
						<span class="payment__option--logos">
							<img src="${base}/skin/images/checkout/Apple_Pay_icon.svg" alt="ApplePay" />
						</span>
					</label>
				</div>
			</div>
		`,
				);
			}

			// Show/hide Apple Pay container based on content
			if (applePayContainer.querySelector('input[name="paymentMethod"]')) {
				applePayContainer.removeAttribute('hidden');
			} else {
				applePayContainer.setAttribute('hidden', '');
			}
			enhancePaymentLabels(applePayContainer);
		}
	});

	// Enhance labels to be focusable radios
	enhancePaymentLabels(registeredContainer);
	enhancePaymentLabels(paypalContainer);

	// Sync selection-driven UI if a radio is prechecked
	const prechecked = (form ?? document).querySelector<HTMLInputElement>(
		'input[name="paymentMethod"]:checked',
	);
	if (prechecked) prechecked.dispatchEvent(new Event('change', { bubbles: true }));

	//CHeck for googlePay and applePay and stop the spinner once logo loads
	void (async () => {
		const [googlePayDeviceEnabled, applePayDeviceEnabled] = await Promise.all([
			window.braintreeFunctions!.doesBrowserSupportGooglePay().catch(() => false),
			window.braintreeFunctions!.doesBrowserSupportApplePay().catch(() => false),
		]);

		const googleAvailable =
			googlePayDeviceEnabled && data.googlePayEnabled === 'true' && data.googlePay;

		const appleAvailableOnly =
			applePayDeviceEnabled &&
			data.applePayEnabled === 'true' &&
			data.applePay &&
			(data.googlePayEnabled === 'false' || !googlePayDeviceEnabled);

		if (googleAvailable || appleAvailableOnly) {
			waitForPaymentBrandLoaded(
				`.form__row--payment-${googleAvailable ? 'google' : 'apple'}-pay img[alt="${googleAvailable ? 'GooglePay' : 'ApplePay'}"]`,
				() => {
					toggleLoadingSpinner(checkoutPaymentElem, null);
				},
			);
		} else {
			toggleLoadingSpinner(checkoutPaymentElem, null);
		}
	})().catch(console.error);

	/**
	 * Waits for an image matching `selector` to appear and finish loading.
	 *
	 * If the image is already in the DOM and loaded, the callback is called immediately.
	 * Otherwise, the DOM is observed until the image appears, and then waits for load/error.
	 *
	 * @param {string} selector - CSS selector for the target image
	 * @param {(img: HTMLImageElement) => void} callback - Called when the image is loaded or errored
	 */
	function waitForPaymentBrandLoaded(
		selector: string,
		callback: (img: HTMLImageElement) => void,
	): void {
		const tryHandle = (): boolean => {
			const img = document.querySelector(selector) as HTMLImageElement | null;
			if (!img) return false;

			if (img.complete) {
				callback(img);
			} else {
				img.addEventListener('load', () => callback(img), { once: true });
				img.addEventListener(
					'error',
					() => callback(img), // fail-safe
					{ once: true },
				);
			}

			return true;
		};

		// Already in DOM?
		if (tryHandle()) return;

		// Otherwise wait for it
		const observer = new MutationObserver(() => {
			if (tryHandle()) {
				observer.disconnect();
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}
}
